import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import ViewQueryModel from "./ViewQueryModel";
import useLinkedTicket from "./dashboard/useLinkedTicket";
import axiosInstance from "../axiosInstance";
import { getImageUrl } from "../imageUrl";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";

import {
  MdSupportAgent,
  MdSearch,
  MdRefresh,
  MdClose,
  MdOutlinePendingActions,
  MdCheckCircleOutline,
  MdOutlineQueryStats,
  MdOutlineErrorOutline,
  MdImage,
  MdOpenInNew,
} from "react-icons/md";

import { RiFilter3Line } from "react-icons/ri";
import { BiCalendar } from "react-icons/bi";

export default function SupportDashboard() {
  const API = process.env.REACT_APP_API_URL;

  // =========================================================
  // STATE
  // =========================================================

  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const isAdmin = localStorage.getItem("userrole") === "admin";
  const [statusFilter, setStatusFilter] = useState("");
  const [preview, setPreview] = useState("");

  // DATE FILTER
  const [dates, setDates] = useState(null);

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");

  // UI STATES
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const closeLinkedTicket = useLinkedTicket(setSelectedQuery, setError);

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (dateString) => {
    if (!dateString || dateString === "null") {
      return "-";
    }

    const d = new Date(dateString);

    if (Number.isNaN(d.getTime())) {
      return "-";
    }

    return (
      d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " " +
      d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  // =========================================================
  // FETCH SUPPORT QUERIES
  // =========================================================

  const fetchQueries = async () => {
    try {
      setLoading(true);
      setError("");

      const depId = localStorage.getItem("department_id");
      const role = localStorage.getItem("userrole");

      if (!depId && role !== "admin") {
        console.error(
          "No department_id found in localStorage"
        );

        setError(
          "Department information is not available."
        );

        return;
      }

      const branchQuery = isAdmin && selectedBranch
        ? `?branch_id=${selectedBranch}`
        : "";
      const res = await axiosInstance.get(
        isAdmin
          ? `${API}/api/query/admin/${branchQuery}`
          : `${API}/api/query/support/${depId}/${branchQuery}`
      );

      setQueries(res.data || []);
    } catch (err) {
      console.error(
        "Error fetching support queries:",
        err
      );

      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Unable to load support queries. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL FETCH
  // =========================================================

  useEffect(() => {
    fetchQueries();
    if (isAdmin) {
      axiosInstance
        .get(`${API}/api/get/branches/`)
        .then((res) => setBranches(res.data || []))
        .catch(() => setError("Unable to load branches."));
    }
  }, [selectedBranch]);

  // =========================================================
  // FILTER DATA
  // =========================================================

  const filterqueries = useMemo(() => {
    let result = [...queries];

    // -------------------------------------------------------
    // DATE RANGE
    // -------------------------------------------------------

    if (dates && dates[0]) {
      const start = new Date(dates[0]);

      start.setHours(0, 0, 0, 0);

      let end;

      if (dates[1]) {
        end = new Date(dates[1]);
      } else {
        end = new Date(dates[0]);
      }

      end.setHours(23, 59, 59, 999);

      result = result.filter((q) => {
        const created = new Date(
          q.query_created_time
        );

        return created >= start && created <= end;
      });
    }

     // -----------------------------
    // STATUS FILTER
    // -----------------------------

    if (statusFilter) {
      const normalizedFilter = statusFilter.toLowerCase();

      result = result.filter((q) => {
        const status = String(q.status || "").toLowerCase();

        if (normalizedFilter === "open") {
          return status !== "completed" && status !== "closed";
        }

        return status === normalizedFilter;
      });
    }

    // -------------------------------------------------------
    // SEARCH
    // -------------------------------------------------------

    if (searchTerm.trim()) {
      const search = searchTerm
        .toLowerCase()
        .trim();

      result = result.filter((q) => {
        return (
          String(q.email || "")
            .toLowerCase()
            .includes(search) ||
          String(q.query_heading || "")
            .toLowerCase()
            .includes(search) ||
          String(q.username || "")
            .toLowerCase()
            .includes(search) ||
          String(q.requested_department_user || "")
            .toLowerCase()
            .includes(search) ||
          String(q.requested_department || "")
            .toLowerCase()
            .includes(search) ||
          String(q.department || "")
            .toLowerCase()
            .includes(search) ||
          String(q.closed_by || "")
            .toLowerCase()
            .includes(search) ||
          String(q.branch || "")
            .toLowerCase()
            .includes(search) ||
          String(q.status || "")
            .toLowerCase()
            .includes(search)
        );
      });
    }

    return result;
  }, [queries, dates, searchTerm,statusFilter]);

  // =========================================================
  // RESET FILTERS
  // =========================================================

  const resetFilters = () => {
    setDates(null);
    setSearchTerm("");
    setStatusFilter("");
  };

  const selectStatFilter = (filter) => {
    setStatusFilter(filter);
  };


  

  // =========================================================
  // STATISTICS
  // =========================================================

  const totalQueries = queries.length;

  const pendingQueries = queries.filter(
    (q) =>
      String(q.status || "").toLowerCase() ===
      "pending"
  ).length;

  const completedQueries = queries.filter(
    (q) =>
      String(q.status || "").toLowerCase() ===
      "completed"
  ).length;

  const openQueries = queries.filter((q) => {
    const status = String(q.status || "").toLowerCase();

    return (
      status !== "completed" &&
      status !== "closed"
    );
  }).length;

  // =========================================================
  // STATUS TEMPLATE
  // =========================================================

  const statusBodyTemplate = (rowData) => {
    const status = String(
      rowData.status || "Unknown"
    );

    const normalizedStatus = status.toLowerCase();

    let style =
      "bg-slate-100 text-slate-700 border-slate-200";

    let dot = "bg-slate-500";

    if (normalizedStatus === "pending") {
      style =
        "bg-amber-50 text-amber-700 border-amber-200";

      dot = "bg-amber-500";
    }

    if (
      normalizedStatus === "completed" ||
      normalizedStatus === "closed"
    ) {
      style =
        "bg-emerald-50 text-emerald-700 border-emerald-200";

      dot = "bg-emerald-500";
    }

    if (
      normalizedStatus === "rejected" ||
      normalizedStatus === "failed"
    ) {
      style =
        "bg-red-50 text-red-700 border-red-200";

      dot = "bg-red-500";
    }

    return (
      <span
        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${style}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${dot}`}
        />

        {status}
      </span>
    );
  };

  // =========================================================
  // EMAIL TEMPLATE
  // =========================================================

  const emailBodyTemplate = (rowData) => {
    return (
      <div
        className="max-w-[220px]"
        title={rowData.email || ""}
      >
        <span className="text-sm text-slate-600 truncate block">
          {rowData.email || "-"}
        </span>
      </div>
    );
  };

  // =========================================================
  // QUERY HEADING
  // =========================================================

  const headingBodyTemplate = (rowData) => {
    return (
      <div
        className="max-w-[220px]"
        title={rowData.query_heading || ""}
      >
        <p className=" text-slate-800 truncate">
          {rowData.query_heading || "-"}
        </p>
      </div>
    );
  };

  // =========================================================
  // USERNAME
  // =========================================================

  const usernameBodyTemplate = (rowData) => {
    const username = rowData.username || "-";

    return (
      <div className="flex items-center gap-2.5">

        <div
          className="w-8 h-8 rounded-full bg-sky-50 border border-sky-100 text-sky-600 flex items-center justify-center text-xs  flex-shrink-0"
        >
          {username !== "-"
            ? username
                .charAt(0)
                .toUpperCase()
            : "?"}
        </div>

        <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
          {username}
        </span>

      </div>
    );
  };

  // REQUESTED USERNAME
  // =========================================================

  const requestedUsernameBodyTemplate = (rowData) => {
    return (
      <div
        className="max-w-[220px]"
        title={rowData.requested_user || ""}
      >
        <p className=" text-slate-800 truncate">
          {rowData.requested_department_user || "-"}
        </p>
      </div>
    );
  };

   // REQUESTED DEPARTMENT
  // =========================================================

  const requestedDepartmentBodyTemplate = (rowData) => {
    return (
      <div
        className="max-w-[220px]"
        title={rowData.requested_department || ""}
      >
        <p className=" text-slate-800 truncate">
          {rowData.requested_department || "-"}
        </p>
      </div>
    );
  };

   
 
  // =========================================================
  // DEPARTMENT
  // =========================================================

  const departmentBodyTemplate = (rowData) => {
    return rowData.department ? (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-medium text-slate-700 whitespace-nowrap"
      >
        {rowData.department}
      </span>
    ) : (
      <span className="text-slate-400">
        —
      </span>
    );
  };

  // Branch
  // =========================================================

  const branchBodyTemplate = (rowData) => {
    return rowData.branch ? (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-medium text-slate-700 whitespace-nowrap"
      >
        {rowData.branch}
      </span>
    ) : (
      <span className="text-slate-400">
        —
      </span>
    );
  }

  // Closed By
  // =========================================================

  const closedByBodyTemplate = (rowData) => {
    return rowData.closed_by ? (
      <span
        className="inline-flex items-center px-2.5 py-1   text-slate-700 whitespace-nowrap "
      >
        {rowData.closed_by}
      </span>
    ) : (
      <span className="text-slate-400">
        —
      </span>
    );
  };

  // =========================================================
  // IMAGE
  // =========================================================

  const imageBodyTemplate = (rowData) => {
    const image = rowData.completed_image || rowData.image;

    if (!image) {
      return (
        <div
          className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center"
        >
          <MdImage
            size={18}
            className="text-slate-400"
          />
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() =>
          setPreview(image)
        }
        title="View attachment"
        className="group relative w-11 h-11 rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
      >
        <img
          src={getImageUrl(image, API)}
          alt="Query attachment"
          className="w-full h-full object-cover transition duration-200 group-hover:scale-110"
        />

        <div
          className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
        >
          <MdImage
            size={16}
            className="text-white"
          />
        </div>
      </button>
    );
  };

  // =========================================================
  // CREATED DATE
  // =========================================================

  const createdDateBodyTemplate = (rowData) => {
    return (
      <span className="text-sm text-slate-600 whitespace-nowrap">
        {formatDate(rowData.query_created_time)}
      </span>
    );
  };

  // =========================================================
  // CLOSED DATE
  // =========================================================

  const closedDateBodyTemplate = (rowData) => {
    return (
      <span className="text-sm text-slate-600 whitespace-nowrap">
        {formatDate(rowData.query_closed_time)}
      </span>
    );
  };

  // =========================================================
  // ACTION
  // =========================================================

  const actionBodyTemplate = (rowData) => {
    return (
      <button
        type="button"
        onClick={() =>
          setSelectedQuery(rowData)
        }
        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold transition whitespace-nowrap"
      >
        <MdOpenInNew size={15} />

        Open
      </button>
    );
  };

  // =========================================================
  // STAT CARD
  // =========================================================

  const StatCard = ({
    title,
    value,
    subtitle,
    icon,
    iconStyle,
    onClick,
  }) => {
    return (
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 w-full text-left bg-white border border-slate-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between gap-2">

          <div className="min-w-0 flex-1">

            <p className="break-words text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">
              {title}
            </p>

            <p className="mt-2 break-words text-xl sm:text-2xl font-bold text-slate-800">
              {value}
            </p>

            <p className="mt-1 text-[10px] sm:text-xs leading-relaxed text-slate-400">
              {subtitle}
            </p>

          </div>

          <div
            className={`w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-lg flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-5 sm:[&>svg]:w-5 ${iconStyle}`}
          >
            {icon}
          </div>

        </div>
      </button>
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="w-full mt-10 min-h-screen bg-slate-50">

      <div className="w-full max-w-[1800px] mx-auto">

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div
          className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 sm:px-5 lg:px-6 py-4 sm:py-5 mb-5"
        >

          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >

            <div className="flex items-center gap-3">

              <div
                className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"
              >
                <MdSupportAgent size={23} />
              </div>

              <div>

                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Support Dashboard
                </h1>

                {/* <p className="text-sm text-slate-500 mt-0.5">
                  Review and manage queries assigned to your department
                </p> */}

              </div>

            </div>

            {/* REFRESH */}

            <button
              type="button"
              onClick={fetchQueries}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 transition"
            >
              <MdRefresh
                size={18}
                className={
                  loading
                    ? "animate-spin"
                    : ""
                }
              />

              Refresh
            </button>

          </div>

        </div>

        {/* =================================================
            KPI CARDS
        ================================================= */}

        <div
          className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-5"
        >

          <StatCard
            title="Total Queries"
            value={totalQueries}
            subtitle="Assigned to department"
            icon={
              <MdOutlineQueryStats size={21} />
            }
            iconStyle="bg-sky-50 text-sky-600"
            onClick={() => selectStatFilter("")}
          />

          <StatCard
            title="Pending"
            value={pendingQueries}
            subtitle="Awaiting support"
            icon={
              <MdOutlinePendingActions size={21} />
            }
            iconStyle="bg-amber-50 text-amber-600"
            onClick={() => selectStatFilter("Pending")}
          />

          <StatCard
            title="Completed"
            value={completedQueries}
            subtitle="Successfully resolved"
            icon={
              <MdCheckCircleOutline size={21} />
            }
            iconStyle="bg-emerald-50 text-emerald-600"
            onClick={() => selectStatFilter("Completed")}
          />

          <StatCard
            title="Open"
            value={openQueries}
            subtitle="Currently active"
            icon={
              <MdSupportAgent size={21} />
            }
            iconStyle="bg-indigo-50 text-indigo-600"
            onClick={() => selectStatFilter("Open")}
          />

        </div>

        {/* =================================================
            FILTER SECTION
        ================================================= */}

        <div
          className="  border-slate-200 rounded-xl p-4 mb-5"
        >

          {/* <div className="flex items-center gap-2 mb-3">

            <RiFilter3Line
              size={18}
              className="text-slate-500"
            />

            <h2 className="text-sm font-semibold text-slate-700">
              Filters
            </h2>

          </div> */}

          <div
            className="flex flex-col lg:flex-row lg:items-center gap-3"
          >

            {/* SEARCH */}

            <div className="relative flex-1">

              <MdSearch
                size={19}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                placeholder="query, department..."
                className="w-1/2 h-10 pl-10 pr-10 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() =>
                    setSearchTerm("")
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-200"
                >
                  <MdClose size={17} />
                </button>
              )}

            </div>

            {/* DATE RANGE */}

            <div className="w-full border  rounded-md lg:w-auto">

              <Calendar
                value={dates}
                onChange={(e) =>
                  setDates(e.value)
                }
                selectionMode="range"
                readOnlyInput
                hideOnRangeSelection
                showIcon
                placeholder="Select date range"
                className="w-full ml-2"
                inputClassName="h-10 text-sm"
              />

            </div>

            {isAdmin && (
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="h-10 w-full lg:w-48 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                aria-label="Filter by branch"
              >
                <option value="">All branches</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            )}

               <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 w-full lg:w-40 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Hold">Hold</option>
              <option value="Completed">Completed</option>
              <option value="Open">Open</option>
            </select>

            {/* RESET */}

            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition"
            >
              <MdRefresh size={17} />

              Reset
            </button>

          </div>

          {/* <div className="mt-3 pt-3 border-t border-slate-100">

            <p className="text-xs text-slate-500">

              Showing{" "}

              <span className="font-semibold text-slate-700">
                {filterqueries.length}
              </span>

              {" "}of{" "}

              <span className="font-semibold text-slate-700">
                {queries.length}
              </span>

              {" "}queries

            </p>

          </div> */}

        </div>

        {/* =================================================
            ERROR STATE
        ================================================= */}

        {error && !loading && (

          <div
            className="bg-white border border-red-200 rounded-xl shadow-sm p-8 mb-5 text-center"
          >

            <div
              className="mx-auto w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center"
            >
              <MdOutlineErrorOutline size={25} />
            </div>

            <h3 className="mt-4 text-sm font-semibold text-slate-800">
              Unable to load support queries
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              {error}
            </p>

            <button
              type="button"
              onClick={fetchQueries}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium"
            >
              <MdRefresh size={17} />
              Try Again
            </button>

          </div>

        )}

        {/* =================================================
            TABLE
        ================================================= */}

        {!error && (

          <div
            className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
          >

            {/* TABLE HEADER */}

            <div
              className="px-4 sm:px-5 py-3.5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            >

              <div>

                <h2 className="text-sm font-semibold text-slate-800">
                  Support Queries
                </h2>

                {/* <p className="text-xs text-slate-500 mt-0.5">
                  Review incoming queries and take action
                </p> */}

              </div>

              <div className="text-xs text-slate-500">
                {filterqueries.length} records
              </div>

            </div>

            {/* LOADING */}

            {loading ? (

              <div className="p-8">

                <div className="space-y-4 animate-pulse">

                  {[1, 2, 3, 4, 5].map(
                    (item) => (
                      <div
                        key={item}
                        className="grid grid-cols-6 gap-4 items-center"
                      >
                        <div className="h-4 bg-slate-100 rounded" />
                        <div className="h-4 bg-slate-100 rounded" />
                        <div className="h-4 bg-slate-100 rounded" />
                        <div className="h-4 bg-slate-100 rounded" />
                        <div className="h-8 bg-slate-100 rounded" />
                        <div className="h-8 bg-slate-100 rounded" />
                      </div>
                    )
                  )}

                </div>

                <p className="text-center text-xs text-slate-400 mt-5">
                  Loading support queries...
                </p>

              </div>

            ) : filterqueries.length === 0 ? (

              /* EMPTY */

              <div className="py-14 px-5 text-center">

                <div
                  className="mx-auto w-14 h-14 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"
                >
                  <MdOutlineQueryStats size={28} />
                </div>

                <h3 className="mt-4 text-sm font-semibold text-slate-800">
                  No support queries found
                </h3>

                <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                  No queries match your current search or date filters.
                </p>

                {(searchTerm || dates) && (

                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50"
                  >
                    <MdRefresh size={17} />
                    Clear Filters
                  </button>

                )}

              </div>

            ) : (

              /* DATA TABLE */

              <div className="w-full overflow-x-auto">

                <DataTable
                  value={filterqueries}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[
                    10,
                    25,
                    50,
                  ]}
                  className="w-full professional-support-table"
                  tableStyle={{
                    minWidth: "1250px",
                  }}
                  rowHover
                  removableSort
                  paginatorTemplate="
                    FirstPageLink
                    PrevPageLink
                    PageLinks
                    NextPageLink
                    LastPageLink
                    RowsPerPageDropdown
                  "
                >

                  {/* EMAIL */}

                  {/* <Column
                    field="email"
                    header="Email"
                    sortable
                    body={emailBodyTemplate}
                    style={{
                      width: "200px",
                    }}
                  /> */}

                  {/* QUERY */}

                  <Column
                    field="query_heading"
                    header="Query"
                    sortable
                    body={headingBodyTemplate}
                    style={{
                      width: "210px",
                    }}
                  />

                  {/* USER */}

                  <Column
                    field="username"
                    header="Created User"
                    sortable
                    body={usernameBodyTemplate}
                    style={{
                      width: "180px",
                    }}
                  />

                  {/* REQUESTED USER */}

                  <Column
                    field="requested_user"
                    header="Requested User"
                    sortable
                    body={requestedUsernameBodyTemplate}
                    style={{
                      width: "180px",
                    }}
                  />

                  {/* REQUESTED DEPARTMENT */}

                  <Column
                    field="requested_department"
                    header="Requested Department"
                    sortable
                    body={requestedDepartmentBodyTemplate}
                    style={{
                      width: "180px",
                    }}
                  />


                  {/* DEPARTMENT */}

                  <Column
                    field="department"
                    header=" Closed Department"
                    sortable
                    body={departmentBodyTemplate}
                    style={{
                      width: "150px",
                    }}
                  />

                  {/* DEPARTMENT */}

                  <Column
                    field="closed_by"
                    header=" Closed By"
                    sortable
                    body={closedByBodyTemplate}
                    style={{
                      width: "150px",
                    }}
                  />

                  {/* IMAGE */}

                  <Column
                    field="image"
                    header="Attachment"
                    body={imageBodyTemplate}
                    style={{
                      width: "110px",
                    }}
                  />

                  {/* BRANCH */}

                  <Column
                    field="branch"
                    header="Branch"
                    sortable
                    body={branchBodyTemplate}
                    style={{
                      width: "130px",
                    }}
                  />

                  {/* STATUS */}

                  <Column
                    field="status"
                    header="Status"
                    sortable
                    body={statusBodyTemplate}
                    style={{
                      width: "130px",
                    }}
                  />

                  {/* CREATED */}

                  <Column
                    field="query_created_time"
                    header="Created Date"
                    sortable
                    body={createdDateBodyTemplate}
                    style={{
                      width: "175px",
                    }}
                  />

                  {/* CLOSED */}

                  <Column
                    field="query_closed_time"
                    header="Closed Date"
                    sortable
                    body={closedDateBodyTemplate}
                    style={{
                      width: "175px",
                    }}
                  />

                  {/* ACTION */}

                  <Column
                    header="Action"
                    body={actionBodyTemplate}
                    style={{
                      width: "120px",
                    }}
                  />

                </DataTable>

              </div>

            )}

          </div>

        )}

      </div>

      {/* =================================================
          IMAGE PREVIEW
      ================================================= */}

      {preview && (

        <div
          className="fixed inset-0 z-[100] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview("")}
        >

          <div
            className="relative max-w-[95vw] max-h-[92vh] bg-white rounded-xl shadow-2xl p-2"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* CLOSE */}

            <button
              type="button"
              onClick={() => setPreview("")}
              className="absolute -right-3 -top-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:text-red-600 hover:bg-red-50 transition"
            >
              <MdClose size={20} />
            </button>

            <img
              src={getImageUrl(preview, API)}
              alt="Query preview"
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            />

          </div>

        </div>

      )}

      {/* =================================================
          VIEW QUERY MODAL
      ================================================= */}

      {selectedQuery && (

        <ViewQueryModel
          query={selectedQuery}
          close={closeLinkedTicket}
          refresh={fetchQueries}
        />

      )}

      {/* =================================================
          PRIME REACT TABLE STYLE
      ================================================= */}

      <style>{`

        .professional-support-table
  .p-datatable-thead > tr > th {

  background: #f8fafc;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 7px 10px !important;
  border-color: #e2e8f0;
  white-space: nowrap;
}

.professional-support-table
  .p-datatable-tbody > tr > td {

  padding: 5px 10px !important;
  border-color: #f1f5f9;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.1 !important;
  vertical-align: middle;
}

        .professional-support-table
          .p-datatable-tbody > tr:hover {

          background: #f8fafc;
        }

        .professional-support-table
          .p-paginator {

          background: #ffffff;
          border: 0;
          border-top: 1px solid #e2e8f0;
          padding: 0.65rem 0.85rem;
          justify-content: flex-end;
          gap: 0.15rem;
        }

        .professional-support-table
          .p-paginator
          .p-paginator-page,
        .professional-support-table
          .p-paginator
          .p-paginator-next,
        .professional-support-table
          .p-paginator
          .p-paginator-prev,
        .professional-support-table
          .p-paginator
          .p-paginator-first,
        .professional-support-table
          .p-paginator
          .p-paginator-last {

          min-width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          font-size: 0.75rem;
        }

        .professional-support-table
          .p-paginator
          .p-highlight {

          background: #0284c7;
          color: white;
        }

        .professional-support-table
          .p-dropdown {

          height: 2rem;
          border-radius: 0.5rem;
          border-color: #e2e8f0;
          font-size: 0.75rem;
        }

        .professional-support-table
          .p-sortable-column-icon {

          width: 0.8rem;
          height: 0.8rem;
        }

        .professional-support-table
          .p-datatable-wrapper {

          scrollbar-width: thin;
        }

        @media (max-width: 640px) {

          .professional-support-table
            .p-datatable-thead > tr > th {

            padding: 0.7rem;
          }

          .professional-support-table
            .p-datatable-tbody > tr > td {

            padding: 0.7rem;
          }

          .professional-support-table
            .p-paginator {

            justify-content: center;
            flex-wrap: wrap;
          }

        }

      `}</style>

    </div>
  );
}
